import type { FC, ReactNode } from 'react'
import type {
    DoorColor,
    DoorMaterial,
    DoorStyle
} from '@/types'

import { useRef, useState } from 'react'
import { RotateCcw, SlidersHorizontal } from 'lucide-react'
import { cn } from '@/lib/utils'
import { gsap, useGSAP, MOTION_QUERY } from '@/lib/gsap'
import { ButtonLink } from '@/components/generals/button'
import { DoorCard } from '@/components/widgets/door-card'
import { QUOTE_PATH } from '@/constants/site'

import {
    doors,
    doorColors,
    doorMaterials,
    doorStyles,
    doorSwatches
} from '@/constants/doors'

export interface DoorFilters {
    style: DoorStyle | null
    material: DoorMaterial | null
    color: DoorColor | null
}

type Filters = DoorFilters

export const EMPTY_DOOR_FILTERS: DoorFilters = { style: null, material: null, color: null }

const EMPTY_FILTERS: Filters = EMPTY_DOOR_FILTERS

interface DoorGalleryProps {
    filters?: DoorFilters
    onFiltersChange?: (filters: DoorFilters) => void
}

interface ChipProps {
    active: boolean
    onClick: () => void
    children: ReactNode
}

const Chip: FC<ChipProps> = ({ active, onClick, children }): ReactNode => {
    return (
        <button
            type='button'
            aria-pressed={active}
            onClick={onClick}
            className={cn(
                'inline-flex h-10 items-center gap-2 rounded-full border px-4 text-[14px] font-medium transition-colors duration-300',
                active ? 'border-ink bg-ink text-cream' : 'border-ink/12 bg-card text-ink hover:border-ink/35'
            )}
        >
            {children}
        </button>
    )
}

export const DoorGallery: FC<DoorGalleryProps> = ({ filters: controlled, onFiltersChange }): ReactNode => {
    const [internal, setInternal] = useState<Filters>(EMPTY_FILTERS)
    const gridRef = useRef<HTMLDivElement>(null)

    const filters = controlled ?? internal

    const setFilters = (next: Filters | ((current: Filters) => Filters)) => {
        const value = typeof next === 'function' ? next(filters) : next

        if (onFiltersChange) onFiltersChange(value)
        if (!controlled) setInternal(value)
    }

    const results = doors.filter(door =>
        (!filters.style || door.style === filters.style)
        && (!filters.material || door.material === filters.material)
        && (!filters.color || door.colors.includes(filters.color))
    )

    const filterKey = `${filters.style}-${filters.material}-${filters.color}`
    const hasFilters = Boolean(filters.style || filters.material || filters.color)

    useGSAP(() => {
        const grid = gridRef.current

        if (!grid) return

        const media = gsap.matchMedia()

        media.add(MOTION_QUERY, () => {
            gsap.from(grid.children, {
                y: 28,
                autoAlpha: 0,
                duration: 0.9,
                ease: 'expo.out',
                stagger: 0.06,
                scrollTrigger: { trigger: grid, start: 'top 88%', once: true }
            })
        })

        return () => media.revert()
    }, { scope: gridRef, dependencies: [filterKey], revertOnUpdate: true })

    const update = <K extends keyof Filters>(key: K, value: Filters[K]) => {
        setFilters(current => ({ ...current, [key]: current[key] === value ? null : value }))
    }

    return (
        <div>
            <div className='rounded-[28px] border border-ink/[0.08] bg-card p-5 sm:p-7'>
                <div className='flex flex-wrap items-center justify-between gap-4'>
                    <p className='inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-slate'>
                        <SlidersHorizontal className='size-3.5' aria-hidden='true' />
                        Narrow down the range
                    </p>

                    <div className='flex items-center gap-4'>
                        <p className='font-mono text-[12px] text-ink' aria-live='polite'>
                            Showing {results.length} of {doors.length} models
                        </p>

                        {hasFilters && (
                            <button
                                type='button'
                                onClick={() => setFilters(EMPTY_FILTERS)}
                                className='inline-flex items-center gap-1.5 text-sm text-slate transition-colors hover:text-ink'
                            >
                                <RotateCcw className='size-3.5' aria-hidden='true' />
                                Reset
                            </button>
                        )}
                    </div>
                </div>

                <div className='mt-6 grid gap-6 lg:grid-cols-3'>
                    <fieldset>
                        <legend className='text-sm font-medium text-ink'>Style</legend>

                        <div className='mt-3 flex flex-wrap gap-2'>
                            {doorStyles.map(style => (
                                <Chip key={style} active={filters.style === style} onClick={() => update('style', style)}>
                                    {style}
                                </Chip>
                            ))}
                        </div>
                    </fieldset>

                    <fieldset>
                        <legend className='text-sm font-medium text-ink'>Material</legend>

                        <div className='mt-3 flex flex-wrap gap-2'>
                            {doorMaterials.map(material => (
                                <Chip key={material} active={filters.material === material} onClick={() => update('material', material)}>
                                    {material}
                                </Chip>
                            ))}
                        </div>
                    </fieldset>

                    <fieldset>
                        <legend className='text-sm font-medium text-ink'>Color</legend>

                        <div className='mt-3 flex flex-wrap gap-2'>
                            {doorColors.map(color => (
                                <Chip key={color} active={filters.color === color} onClick={() => update('color', color)}>
                                    <span
                                        className='size-3.5 rounded-full ring-1 ring-ink/20'
                                        style={{ backgroundColor: doorSwatches[color] }}
                                        aria-hidden='true'
                                    />
                                    {color}
                                </Chip>
                            ))}
                        </div>
                    </fieldset>
                </div>
            </div>

            {results.length > 0 ? (
                <div ref={gridRef} className='mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-3'>
                    {results.map(door => (
                        <DoorCard key={`${door.id}-${filters.color ?? 'any'}`} door={door} preferredColor={filters.color} />
                    ))}
                </div>
            ) : (
                <div className='mt-8 flex flex-col items-start gap-6 rounded-[28px] border border-dashed border-ink/20 p-8 sm:p-12'>
                    <p className='max-w-xl font-heading text-2xl font-semibold leading-snug tracking-[-0.03em]'>
                        Nothing fits that combination. Loosen a filter, or ask us: we can order more than 200 other styles.
                    </p>

                    <div className='flex flex-wrap gap-3'>
                        <button
                            type='button'
                            onClick={() => setFilters(EMPTY_FILTERS)}
                            className='inline-flex h-12 items-center gap-2 rounded-full border border-ink/15 px-5 text-[15px] font-medium transition-colors hover:border-ink/40'
                        >
                            <RotateCcw className='size-4' aria-hidden='true' />
                            Reset all filters
                        </button>

                        <ButtonLink href={`${QUOTE_PATH}?service=new-doors&entry=help_me_choose`}>
                            Help me pick a door
                        </ButtonLink>
                    </div>
                </div>
            )}
        </div>
    )
}
